"use client";
import { checkEmailExists } from "@/app/actions/checkEmailExists";
import { auth, db } from "@/firebase/firebase";
import { sendVerificationEmail } from "@/store/feature/user/userApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { allowedDomains } from "@/utils/Constants";
import { doc, updateDoc } from "firebase/firestore";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { EditPencilWithOutCircle, EmailResend } from "./Icons";

const EMAIL_VERIFICATION_CONTEXT_KEY = "pendingEmailVerification";

type PendingEmailVerification = {
  userId?: string;
  email?: string;
  phoneNumber?: string | null;
  requestedAt?: string;
};

interface ErrorWithResponse {
  response?: {
    data?: {
      message?: string;
      customToken?: string;
    };
  };
  message?: string;
}

type ModalView = "initial" | "success";

const isAllowedEmail = (email: string): boolean => {
  const domain = email.split("@")[1]?.toLowerCase();
  return domain ? allowedDomains.includes(domain) : false;
};

interface EmailVerificationProps {
  setEmailVerificationPopup: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function EmailVerification({
  setEmailVerificationPopup,
}: EmailVerificationProps) {
  const params = usePathname();

  const dispatch = useAppDispatch();
  const { userDetails } = useAppSelector((state) => state.userProfile);
  const [pendingEmail, setPendingEmail] = useState("");
  const [storedVerificationContext, setStoredVerificationContext] =
    useState<PendingEmailVerification | null>(null);

  const prefilledEmail = useMemo(() => {
    return (
      userDetails?.email?.trim() ||
      auth.currentUser?.email?.trim() ||
      storedVerificationContext?.email?.trim() ||
      pendingEmail ||
      ""
    );
  }, [pendingEmail, storedVerificationContext?.email, userDetails?.email]);

  const [newEmail, setNewEmail] = useState("");
  const [errorText, setErrorText] = useState("");
  const [modalView, setModalView] = useState<ModalView>("initial");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      const storedContext = localStorage.getItem(
        EMAIL_VERIFICATION_CONTEXT_KEY,
      );
      const pendingVerification: PendingEmailVerification | null = storedContext
        ? JSON.parse(storedContext)
        : null;

      setStoredVerificationContext(pendingVerification);

      const activeUserId =
        userDetails?.user_id ||
        auth.currentUser?.uid ||
        pendingVerification?.userId;

      if (
        pendingVerification?.userId &&
        pendingVerification.userId === activeUserId
      ) {
        setPendingEmail(pendingVerification.email?.trim() || "");
      } else {
        setPendingEmail("");
      }
    } catch (error) {
      console.error(
        "Failed to read pending email verification context:",
        error,
      );
      setStoredVerificationContext(null);
      setPendingEmail("");
    }
  }, [userDetails?.user_id]);

  useEffect(() => {
    setNewEmail((currentEmail) => currentEmail || prefilledEmail);
  }, [prefilledEmail]);

  const getErrorMessage = useCallback((error: unknown): string => {
    const err = error as ErrorWithResponse;
    return (
      err?.response?.data?.message ||
      err?.message ||
      "Something went wrong. Please try again."
    );
  }, []);

  const verifyEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const email = newEmail.trim().toLowerCase();
    const storedContext = localStorage.getItem(EMAIL_VERIFICATION_CONTEXT_KEY);
    const pendingVerification: PendingEmailVerification | null = storedContext
      ? JSON.parse(storedContext)
      : null;
    const resolvedUserId =
      userDetails?.user_id ||
      auth.currentUser?.uid ||
      pendingVerification?.userId;
    const resolvedPhoneNumber =
      userDetails?.primary_phone_number ??
      auth.currentUser?.phoneNumber ??
      pendingVerification?.phoneNumber ??
      null;
    const normalizedCurrentEmail =
      userDetails?.email?.trim().toLowerCase() ||
      auth.currentUser?.email?.trim().toLowerCase() ||
      pendingVerification?.email?.trim().toLowerCase() ||
      "";
    const normalizedPendingEmail =
      pendingVerification?.email?.trim().toLowerCase() || "";
    const normalizedNewEmail = email.toLowerCase();

    if (!email) {
      setErrorText("Email is required.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorText("Please enter a valid email address.");
      setLoading(false);
      return;
    }

    if (!isAllowedEmail(email)) {
      setErrorText(
        "Please use an email address from Gmail, Outlook, Microsoft, Hotmail, or ProtonMail.",
      );
      setLoading(false);

      return;
    }

    setLoading(true);
    setErrorText("");

    try {
      const { exists, uid, error } = await checkEmailExists(email);
      if (error) throw new Error(error);

      const matchesPendingVerification =
        !!resolvedUserId &&
        pendingVerification?.userId === resolvedUserId &&
        normalizedNewEmail === normalizedPendingEmail;
      const matchesCurrentUserEmail =
        normalizedNewEmail === normalizedCurrentEmail;
      const belongsToSameFirebaseUser =
        !!uid && !!resolvedUserId && uid === resolvedUserId;
      const isAllowedExistingEmail =
        exists &&
        (belongsToSameFirebaseUser ||
          matchesPendingVerification ||
          matchesCurrentUserEmail);

      if (exists && !isAllowedExistingEmail) {
        setErrorText("This email address is already in use.");
        setLoading(false);
        return;
      }

      localStorage.setItem(
        EMAIL_VERIFICATION_CONTEXT_KEY,
        JSON.stringify({
          userId: resolvedUserId,
          email,
          phoneNumber: resolvedPhoneNumber,
          requestedAt: new Date().toISOString(),
        }),
      );
      setStoredVerificationContext({
        userId: resolvedUserId,
        email,
        phoneNumber: resolvedPhoneNumber,
        requestedAt: new Date().toISOString(),
      });

      try {
        await dispatch(
          sendVerificationEmail({
            email,
            phoneNumber: resolvedPhoneNumber,
            redirectTo: params || null,
          }),
        ).unwrap();
      } catch (apiError) {
        console.error("Verification email API error:", apiError);
        throw apiError;
      }

      try {
        if (resolvedUserId) {
          await updateDoc(doc(db, "users", resolvedUserId), {
            email,
          });
        }
      } catch (firestoreError) {
        console.error(
          "Verification email Firestore sync error:",
          firestoreError,
        );
      }

      setModalView("success");
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error verifying email:", error);
      setErrorText(getErrorMessage(error));
    }
  };
  const handleClose = () => setEmailVerificationPopup(false);

  if (modalView === "success") {
    return (
      <div className="ff_poppins bg-white rounded-2xl py-6 sm:py-10 px-6 sm:px-8 md:px-16 relative z-[110] max-w-[550px]">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="font-semibold text-xl sm:text-2xl mb-2">
            Email Sent Successfully!
          </h2>
          <p className="text-sm sm:text-base text-gray-700 mb-2">
            A verification link has been sent to:
          </p>
          <p className="text-[#3777FF] font-semibold mb-4">{newEmail}</p>
          <p className="text-sm text-gray-600 mb-6">
            Please check your inbox and click the verification link to complete
            the process.
          </p>
          <button
            onClick={handleClose}
            className="w-full py-3 bg-[#3777FF] text-white rounded-full font-medium hover:bg-[#2f66d0] transition"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="ff_poppins bg-white w-full max-w-[330px] sm:max-w-[450px] lg:max-w-[500px] xl:max-w-[500px] p-6 sm:p-10 md:p-11 lg:p-12 xl:p-14 rounded-xl relative z-100
      flex flex-col items-center justify-center overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Top curved background */}
      <span className="bg-[#E5E8FD] w-full h-full max-w-[470px] max-h-[300px] sm:max-h-[350px] lg:max-h-[400px] absolute rounded-full top-[-52%] left-[50%] -translate-x-1/2"></span>

      <div className="z-100 flex flex-col items-center justify-center text-center">
        <span className="inline-block max-w-[120px] sm:max-w-[140px] lg:max-w-[150px] xl:max-w-[162px]">
          <EmailResend />
        </span>

        <h4 className="mb-4 text-base font-semibold sm:text-lg">
          Add Your Email
        </h4>
        <p className="text-xs sm:text-sm md:text-base text-center text-gray-600 mb-6">
          Add an email address for notifications and account recovery.
        </p>

        <form onSubmit={verifyEmail} className="w-full flex flex-col gap-3">
          <div className="w-full relative">
            <span className="absolute right-[2%] top-[45%] -translate-y-1/2">
              <EditPencilWithOutCircle color="#3777FF" />
            </span>
            <input
              value={newEmail}
              onChange={(e) => {
                setErrorText("");
                setNewEmail(e.target.value);
              }}
              className="w-full rounded-md border border-gray-300 bg-white py-3 pl-3 pr-9 text-base text-[#0C182B] placeholder:text-gray-400 focus:ring-2 focus:ring-[#3777FF] outline-none"
              placeholder="Enter your email"
              disabled={loading}
            />
          </div>

          {errorText && (
            <p className="text-red-600 text-sm text-start">{errorText}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full cursor-pointer py-3 text-white rounded-full font-medium bg-button-gradient transition ${
              loading ? "cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            {loading ? "Processing..." : "Continue"}
          </button>
        </form>
      </div>
    </div>
  );
}
